import type { TowerType, EnemyType } from '../core/types';

/** Base forest palette */
export const GRASS = 0x2e4a1f;
export const DIRT = 0x7a5a36;

/**
 * Resting opacity of an (un-highlighted) tower plot marker. Kept low so the
 * build slots blend into the background instead of dominating it; placement
 * mode brightens them via tint + alpha pulse.
 */
export const PLOT_IDLE_ALPHA = 0.3;

/**
 * Which way a tower's art is drawn, for the sprites that get MIRRORED in play:
 * +1 faces right, -1 faces left. A type absent from this table is never
 * mirrored, which is every tower that stands still on its plot.
 *
 * Only the capsule's two MELEE capybaras are listed (specs 2026-09-16 and
 * 2026-09-17): they charge their target, and a capybara biting backwards over
 * his own shoulder looked wrong. The mirror is driven by a melee unit's
 * displacement as it runs, so listing a tower that never leaves its plot would
 * do nothing — which is why Mage Capy, Mini Gunner and King Capy are absent
 * even though Mini Gunner's minigun points the other way to everything else.
 * All the tower sheets are drawn facing LEFT; the minigame's running frames
 * face RIGHT, which is why this is a per-key fact and not a constant.
 */
export const SPRITE_FACING: Partial<Record<TowerType, 1 | -1>> = {
  capy: -1,
  hog: -1,
  // The Golden Takeout Bag's melee pair are painted facing RIGHT.
  dagger: 1,
  samurai: 1,
};

/** Accent colour per tower type (used for placeholder textures and UI tinting). */
export const TOWER_COLORS: Record<TowerType, number> = {
  patty: 0x8b4513, // saddle brown — beef patty
  pickle: 0x4a7c2f, // dark green — pickle
  cheese: 0xf5c518, // amber — cheese
  bacon: 0xc0392b, // crimson — bacon
  sauce: 0xe55e24, // burnt orange — hot sauce
  blade: 0xd98f00, // deep cheddar-gold — the Cheese Bladesmen
  bomb: 0xb5442a, // smoky BBQ red-orange — the BBQ Bomber
  wise: 0x9ec9ff, // serene sky-blue — the meditating Wise Hambi
  wizard: 0x8e5bd6, // arcane violet — the Wizard Tower
  sniper: 0x5b6650, // gunmetal olive — the General Sniper
  darkhambi: 0x5a2d82, // dark violet — the Dark Hambi's aura
  ninja: 0xc0392b, // red headband crimson — the Ninja Hambi
  sensei: 0xd42a1f, // ketchup red — Sensei Ketchup
  boxer: 0xe03a2f, // glossy glove red — Boxer Hambi
  gunner: 0x4a6fa5, // blue-steel pistol — Gunner Hambi
  firemage: 0xf07818, // blazing amber-orange — the Fire Mage's flame aura
  aquasorcerer: 0x3aa8d8, // clear water blue — the Aqua Sorcerer's orbiting ring
  shrekbook: 0x4a7c1f, // swamp green — Shrekbook Hambi's book and aura
  phantom: 0x7fd8e8, // spectral pale cyan — Phantom Hambi's translucent glass burger
  lightning: 0x8fb8e8, // storm-cloud blue — Lightning Hambi's rain and bolts
  spear: 0xf28c28, // flame orange — Spear Hambi's blazing spear (tiers 2–3)
  bow: 0x7ccd3a, // toxic green — Bow Hambi's poison arrow
  capy: 0xb5713c, // chestnut brown — Capy's own fur
  generalcapy: 0x7d8f4a, // olive drab — General Capy's helmet
  galacticcapy: 0xa04ae0, // portal violet — Galactic Capy's ring
  hog: 0xc47a3f, // toasted bun brown — the burger riding on Hog Hambi's back
  magecapy: 0x5b4ac4, // starry indigo — Mage Capy's wizard hat
  minigunner: 0x6b7a5a, // gunmetal olive — Mini Gunner's helmet and minigun
  kingcapy: 0xc0143c, // ruby red — King Capy's gems and his throne
  kinghambi: 0xffd21f, // royal yellow — King Hambi's shards and his crown
  dagger: 0x9aa7b4, // steel grey — Dagger Hambi's blade
  leafstorm: 0xe0701f, // autumn orange — Leafstorm Hambi's whirl of leaves
  samurai: 0xc8242c, // headband red — Samurai Slicer
  masher: 0xf2ecd2, // mayo cream — Mayo Masher's jar
  grease: 0x9aa4ad, // spatula steel — the Grease Goddess
  ultrahammy: 0x8a4ae0, // crystal violet — Ultra Hammy's tier-4 aura
  horror: 0x8b0f1a, // dark blood red — his poison balls
  demonic: 0x5b2a86, // dark purple — his skulls' blast
  hamreaper: 0x2a1424, // near-black crimson — the dark flaming skulls
  healer: 0x3fd98a, // healing green — his staff's crystal
  banker: 0x6fae55, // banknote green
  angelic: 0xffe9a0, // pale gold — his halo and fireballs
  goldenshroom: 0xffc93a, // the gold cap
};

/** Accent colour per enemy type. */
export const ENEMY_COLORS: Record<EnemyType, number> = {
  grunt: 0x7f8c8d, // slate grey
  dino: 0x27ae60, // emerald green
  fry: 0xf1c40f, // bright yellow — golden fry
  hotdog: 0xe67e22, // carrot orange — hotdog
  boss: 0x8e44ad, // wisteria purple — big boss
  frost: 0xbfe3f2, // icy pale blue — snow-covered Frozen Nugget
  icedino: 0x8fd8ea, // glacial cyan — the ice-cubed Frozen Dino
  driller: 0xa06ce0, // faceted amethyst — the Dripstone Driller
  nullnugget: 0x4a4a55, // void grey — the Null Nugget boss
  ultranugget: 0xe8642c, // crystal orange — the Ultra Nugget
  jockey: 0x8a3a2e, // spider rust-red — the Nugget Jockey
  watcher: 0xbe63e8, // glowing amethyst — the Crystal Watcher
  nuggetguard: 0xc9a24b, // burnished helm gold — the Nugget Guard
  kingnuggetron: 0xffd23f, // royal crown gold — King Nugget-tron
  carrotcrusher: 0xf5811e, // bright carrot orange — the Carrot Crusher
  carrotninja: 0xd2601a, // deeper burnt orange — the Carrot Ninja reads apart from the Crusher
  brocco: 0x2e8b57, // broccoli sea-green — the Brocco
  potatoslicer: 0xb8863b, // earthy potato brown-gold — the Potato Slicer
  drycarrot: 0xcbb98a, // dusty beige — the desert-rotted Dry Carrot
  minibell: 0xe23b2e, // bright bell red — the Mini Bell
  emperorpepro: 0xb2140c, // imperial pepper crimson — Emperor Pepro
  caulifire: 0x3a2f2b, // charred coal-black — Caulifire's burnt florets
  esparagusmage: 0x7b4fc4, // arcane purple — the Esparagus Mage's stars and hat
  ocraa: 0x6f9c3a, // ridged okra green — the Ocraa viking
  bamboocrew: 0x9fb84e, // pale bamboo yellow-green — the Bamboo Crew
  peproherald: 0xb2140c, // the emperor's own crimson — it IS him, riding ahead
  kingember: 0x2f8fe6, // blue-flame azure — King Ember, the final boss
  shrekbruit: 0x5aa832, // all-green brute — the arena-only Shrekbruit
  cosmiccorroder: 0x7b3fbf, // deep violet — the purple Cosmic Corroder
  nuisancenugget: 0x2b2b2b, // top-hat black — the Nuisance Nugget
  barwaiter: 0x6a4c93, // wine-and-broccoli plum — the Bar Waiter
  burntfry: 0x3b3b3b, // charred black — the Burnt Fry
  microwavednugget: 0xe0452a, // microwave-hot red — the Microwaved Nugget's top half
  combo199: 0xf28c28, // nugget-box orange — the 1.99 Combo
  chiply: 0xe8c547, // crisp gold — Chiply the Chip
  microwave: 0x4aa3ff, // pixel-face blue — The Microwave
  chiplysdad: 0xd9a92a, // crowned crisp gold — Chiply's Dad
  nacho: 0xf2a541, // corn-chip orange — Nacho
  seasonezchippington: 0xc9b458, // peppered gold — Seasonez Chippington
  lifeguard: 0xd93b2b, // cap red — The Lifeguard
  moonnacho: 0x9aa0a8, // moon grey — Moon Nacho
  spicednacho: 0xe23c26, // chili red — Spiced Nacho
  lasernacho: 0xf2c14a,
  // Prince Nacho: royal gold, and a deeper crown gold for the second form.
  princenacho: 0xffc53d,
  princenacho2: 0xe8a317, // corn gold under black shades — Laser Nacho
  ghost: 0xe8eef5, // sheet white — the Ghost
  thedead: 0x8a1414, // blood red — The Dead
  darkghost: 0xb3171d, // crimson — the Dark Ghost
  darknacho: 0x7a0f14, // blood red — the Dark Nacho
  jackoghost: 0xf08a1c, // pumpkin orange — Jack'O Ghost
  blueghost: 0x8fd3ff, // ice blue — the Blue Ghost
  reaper: 0x5b2a86, // wizard-hat purple — The Shadow Reaper
  winger: 0xf08a24, // carrot orange — Winger
  twilight: 0xffe14a, // glowing yellow — Twilight
  nightfly: 0x22252b, // night black — Nightfly
  halolight: 0xfff1a8, // pale gold outline — Halolight and his splits
  halolight2: 0xfff1a8,
  halolight3: 0xfff1a8,
  lordturtleshell: 0x5c9a4a, // shell green
  redshroom: 0xd8362a, // red cap
  poisonshroom: 0x3fc93a, // toxic green cap
  skullshroom: 0x2a2a2e, // black cap
  purplefungi: 0x5a2a7a, // deep plum cap
  themushy: 0x8a5a2b, // brown cap
  glowshroom: 0xff9a1f, // glowing orange cap
  blizzardfungi: 0x9fd6f0, // icy blue cap
  zombieshroom: 0x3f5f2a, // rotten dark green cap
  hynofungi: 0x2f9bff, // glowing blue cap
  kingshroometron: 0x5a3a1e, // half black, half brown
};
